import { ArchiveRestore, Pause, Play, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InstanceStatusBadge } from '@/components/StatusBadge';
import { Badge, Button, Card, DataTable, Modal, type DataTableColumn } from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type Instance } from '@/lib/api';
import { formatDateTime, formatPlanBandwidth } from '@/lib/format';
import { paths } from '@/router/paths';
import { AdminSkeleton, useAdminData } from './AdminLayout';

export function AdminInstancesPage() {
  const t = useT();
  const { instances, reload } = useAdminData();
  const notify = useNotify();
  const [purgeTarget, setPurgeTarget] = useState<Instance | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = async (instance: Instance) => {
    try {
      if (instance.status === 'SUSPENDED') {
        await api.admin.resume(instance.id);
        notify.success(t('admin.resumed', { name: instance.name }));
      } else {
        await api.admin.suspend(instance.id, 'Durch Administrator ausgesetzt');
        notify.success(t('admin.suspended', { name: instance.name }));
      }
      await reload();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    }
  };

  const restore = async (instance: Instance) => {
    setBusy(true);
    try {
      await api.admin.restore(instance.id);
      notify.success(t('admin.restored', { name: instance.name }));
      await reload();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const purge = async (instance: Instance) => {
    setBusy(true);
    try {
      await api.admin.purge(instance.id);
      notify.success(t('admin.purged', { name: instance.name }));
      setPurgeTarget(null);
      await reload();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<Instance>[] = [
    {
      key: 'name',
      header: t('instances.columnInstance'),
      render: (row) => (
        <div className="min-w-0">
          <Link
            to={paths.instances.detail(row.id)}
            className="truncate font-bold text-t0 hover:text-acc"
          >
            {row.name}
          </Link>
          <p className="truncate font-mono text-[11.5px] text-t2">{row.ifName}</p>
        </div>
      ),
    },
    {
      key: 'owner',
      header: t('admin.customer'),
      hideBelow: 'md',
      render: (row) => <span className="text-t1">{row.owner?.email ?? '-'}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <InstanceStatusBadge status={row.status} />
          {row.isDedicated && <Badge variant="accent">{t('instances.dedicated')}</Badge>}
          {row.externalRef && <Badge variant="info">WHMCS #{row.externalRef}</Badge>}
          {row.status === 'DELETED' && row.deleteAfterAt && (
            <span className="text-[11px] text-t2">
              {t('admin.trashUntil', { date: formatDateTime(row.deleteAfterAt) })}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'plan',
      header: t('instances.columnPlan'),
      hideBelow: 'lg',
      render: (row) => (
        <div>
          <p className="text-t0">{row.plan.name}</p>
          <p className="text-[11.5px] text-t2">{formatPlanBandwidth(row.bandwidthLimitMbps)}</p>
        </div>
      ),
    },
    {
      key: 'net',
      header: t('admin.net'),
      hideBelow: 'lg',
      render: (row) => (
        <div className="font-mono text-[11.5px]">
          <p className="text-t1">{row.ipv4Cidr}</p>
          {row.dedicatedIp && <p className="text-acc">{row.dedicatedIp}</p>}
        </div>
      ),
    },
    {
      key: 'peers',
      header: t('instances.columnPeers'),
      render: (row) => (
        <span className="tabular-nums text-t1">
          {row.peerCount} / {row.maxPeers}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '110px',
      render: (row) =>
        row.status === 'DELETED' ? (
          // Papierkorb: wiederherstellen oder sofort endgueltig loeschen.
          <div className="flex justify-end gap-1">
            <button
              type="button"
              title={t('admin.restore')}
              aria-label={t('admin.restore')}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                void restore(row);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] text-ok hover:bg-bg-3"
            >
              <ArchiveRestore size={15} />
            </button>
            <button
              type="button"
              title={t('admin.purge')}
              aria-label={t('admin.purge')}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                setPurgeTarget(row);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] text-bad hover:bg-bg-3"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            title={row.status === 'SUSPENDED' ? t('admin.resume') : t('admin.suspend')}
            aria-label={row.status === 'SUSPENDED' ? t('admin.resume') : t('admin.suspend')}
            onClick={(event) => {
              event.stopPropagation();
              void toggle(row);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] text-t1 hover:bg-bg-3"
          >
            {row.status === 'SUSPENDED' ? (
              <Play size={15} className="text-ok" />
            ) : (
              <Pause size={15} className="text-warn" />
            )}
          </button>
        ),
    },
  ];

  return (
    <AdminSkeleton>
      <Card padding="none">
        <DataTable
          columns={columns}
          data={instances}
          rowKey={(row) => row.id}
          emptyMessage={t('instances.emptyTitle')}
        />
      </Card>

      <Modal
        open={Boolean(purgeTarget)}
        onClose={() => setPurgeTarget(null)}
        title={t('admin.purgeTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPurgeTarget(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => purgeTarget && void purge(purgeTarget)}
            >
              {t('admin.purge')}
            </Button>
          </div>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-t1">
          {t('admin.purgeText', { name: purgeTarget?.name ?? '' })}
        </p>
      </Modal>
    </AdminSkeleton>
  );
}
